import { Controller } from "@hotwired/stimulus";
import { v4 } from "uuid";
import getValueFromLocalStorage from "../functions/getValueFromLocalStorage";

export default class extends Controller {
  static get targets() {
    return ["input", "list", "listTitle"];
  }
  connect() {
    this.populateInitialDomains();
    this.updateListTitleVisibility();
  }

  populateInitialDomains() {
    const initialDomains = JSON.parse(this.element.dataset.initialDomains || "[]");
    initialDomains.forEach(domain => this.addDomainToList(domain));
  }

  addDomain(event) {
    event.preventDefault();
    const domain = this.inputTarget.value.trim().toLowerCase();

    if (domain && this.isValidDomain(domain) && !this.domainExists(domain)) {
      this.addDomainToList(domain);
      this.inputTarget.value = "";
      this.updateListTitleVisibility();
    } else if (!this.isValidDomain(domain)) {
      this.showNotification("Please enter a valid domain name");
    } else if (this.domainExists(domain)) {
      this.showNotification("This domain has already been added");
    }
  }

  isValidDomain(domain) {
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;
    return domainRegex.test(domain);
  }

  removeDomain(event) {
    const domainItem = event.target.closest(".list-item");
    if (domainItem) {
      domainItem.remove();
      this.updateListTitleVisibility();
    }
  }

  async saveChanges() {
    const currentDomains = this.getDomains();
    const initialDomains = JSON.parse(this.element.dataset.initialDomains || "[]");

    if (this.arraysEqual(initialDomains, currentDomains)) {
      this.showNotification("No changes to save");
      return;
    }

    this.showOverlay();

    const jsonData = this.createJsonData(currentDomains);
    await this.sendRequest(this.element.dataset.saveUrl, "POST", jsonData);

    location.reload();
  }

  getDomains() {
    return Array.from(this.listTarget.querySelectorAll(".domain-name"))
      .map(element => element.textContent.trim());
  }

  showOverlay() {
    this.element.dispatchEvent(new CustomEvent("overlay:show", { bubbles: true }));
  }

  hideOverlay() {
    this.element.dispatchEvent(new CustomEvent("overlay:hide", { bubbles: true }));
  }

  createJsonData(domains) {
    const csrfToken = this.element.querySelector("input[name=\"_csrf_token\"]").value;
    const mnemonicValue = getValueFromLocalStorage("ab_hd_private_key_mnemonic");

    return {
      _csrf_token: csrfToken,
      change_request_id: v4(),
      sign: { mnemonic: mnemonicValue },
      permitted_email_extensions: { domains }
    };
  }

  async sendRequest(url, method, jsonData) {
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(jsonData)
    });

    return await response.json();
  }

  domainExists(domain) {
    return Array.from(this.listTarget.querySelectorAll(".domain-name"))
      .some(element => element.textContent.trim().toLowerCase() === domain.toLowerCase());
  }

  addDomainToList(domain) {
    const li = document.createElement("li");
    li.className = "list-item";
    li.innerHTML = `
      <span class="domain-name">${domain}</span>
      <button class="remove-button"
              type="button" 
              data-action="click->domain-restrictions#removeDomain" 
              data-domain="${domain}">×</button>
    `;
    this.listTarget.appendChild(li);
  }

  updateListTitleVisibility() {
    this.listTitleTarget.style.display = this.listTarget.children.length > 0 ? "block" : "none";
  }

  showNotification(message) {
    document.dispatchEvent(new CustomEvent("notification:add", { detail: { message } }));
  }

  arraysEqual(arr1, arr2) {
    return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
  }
}
